import { BOARD_TOOLS, BoardStroke, BoardTool, BrushTool, TextFont, isPixelTool } from "../../shared/boards";
import { RetouchKind, RetouchLook, RetouchState, freshRetouch, retouchStroke } from "./whiteboard_retouch";

// how each brush draws. a stroke is a row of circles, one per point, joined by their tangent lines, all in one
// path that is filled once, so a see-through marker never darkens where it crosses itself and pressure is just
// a different circle size per point. on top of that a brush can have soft edges (a few fills, each a bit
// narrower), a texture (the fill is a pattern with holes in it), scattered dots instead of a line, or move
// with the clock. every shape is built once per stroke and kept, so zooming and panning only re-fill it

// "fill" is the lasso fill's own kind of stroke and never shows on the brush shelf
export type BrushGroup = "draw" | "paint" | "texture" | "live" | "retouch" | "fill";
type Texture = "none" | "grain" | "chalk" | "crayon" | "charcoal" | "paper";
type Scatter = "" | "spray" | "air" | "stipple";
type Anim = "" | "rainbow" | "glitter" | "neon" | "boil" | "wave" | "flame" | "march" | "glitch" | "pulse";

export interface ToolFeel {
  label: string;
  icon: string;
  group: BrushGroup;
  // narrowest a stroke gets at no pressure, as a share of its size
  thin: number;
  // how much pressure matters by default, 0 to 1
  pressure: number;
  // how much the line trails the pen to smooth out shaky hands, 0 to 1
  smooth: number;
  // thin ends, like a real brush
  taper: boolean;
  // soft edges 0 to 1
  soft: number;
  texture: Texture;
  scatter: Scatter;
  // how far apart scattered dots are stamped
  gap: number;
  anim: Anim;
  blend: GlobalCompositeOperation;
  size: number;
  opacity: number;
}

type Spec = Partial<ToolFeel> & Pick<ToolFeel, "label" | "icon" | "group" | "size" | "opacity">;

function feel(spec: Spec): ToolFeel {
  return { thin: 0.3, pressure: 1, smooth: 0.3, taper: false, soft: 0, texture: "none", scatter: "", gap: 0.3, anim: "", blend: "source-over", ...spec };
}

export const TOOL_FEEL: Record<BrushTool, ToolFeel> = {
  pen: feel({ label: "pen", icon: "wb-pen", group: "draw", thin: 0.22, smooth: 0.35, size: 5, opacity: 1 }),
  pencil: feel({ label: "pencil", icon: "wb-pencil", group: "draw", thin: 0.45, smooth: 0.15, texture: "grain", size: 4, opacity: 0.9 }),
  ink: feel({ label: "ink", icon: "wb-ink", group: "draw", thin: 0.08, smooth: 0.6, taper: true, size: 9, opacity: 1 }),
  marker: feel({ label: "marker", icon: "wb-marker", group: "draw", thin: 1, pressure: 0, smooth: 0.4, size: 14, opacity: 0.75 }),
  highlighter: feel({ label: "glow", icon: "wb-highlight", group: "draw", thin: 1, pressure: 0, smooth: 0.5, blend: "multiply", size: 26, opacity: 0.4 }),
  eraser: feel({ label: "eraser", icon: "wb-eraser", group: "draw", thin: 0.4, smooth: 0.2, blend: "destination-out", size: 24, opacity: 1 }),

  brush: feel({ label: "brush", icon: "wb-brush", group: "paint", thin: 0.04, smooth: 0.55, taper: true, size: 18, opacity: 1 }),
  watercolor: feel({ label: "water", icon: "wb-water", group: "paint", thin: 0.5, pressure: 0.5, smooth: 0.6, soft: 0.7, texture: "paper", size: 30, opacity: 0.55 }),
  airbrush: feel({ label: "air", icon: "wb-air", group: "paint", thin: 0.5, pressure: 0.7, smooth: 0.5, scatter: "air", gap: 0.12, size: 40, opacity: 0.5 }),
  spray: feel({ label: "spray", icon: "wb-spray", group: "paint", thin: 0.6, pressure: 0.4, smooth: 0.3, scatter: "spray", gap: 0.25, size: 36, opacity: 0.9 }),

  charcoal: feel({ label: "coal", icon: "wb-charcoal", group: "texture", thin: 0.4, pressure: 0.9, smooth: 0.25, soft: 0.25, texture: "charcoal", size: 16, opacity: 0.85 }),
  chalk: feel({ label: "chalk", icon: "wb-chalk", group: "texture", thin: 0.7, pressure: 0.5, smooth: 0.25, texture: "chalk", size: 14, opacity: 0.95 }),
  crayon: feel({ label: "crayon", icon: "wb-crayon", group: "texture", thin: 0.6, pressure: 0.6, smooth: 0.3, texture: "crayon", size: 12, opacity: 0.95 }),
  stipple: feel({ label: "dots", icon: "wb-stipple", group: "texture", thin: 0.6, pressure: 0.8, smooth: 0.3, scatter: "stipple", gap: 0.55, size: 20, opacity: 1 }),

  rainbow: feel({ label: "rainbow", icon: "wb-rainbow", group: "live", thin: 0.3, smooth: 0.5, anim: "rainbow", size: 16, opacity: 1 }),
  glitter: feel({ label: "glitter", icon: "wb-glitter", group: "live", thin: 0.4, smooth: 0.4, soft: 0.2, anim: "glitter", size: 18, opacity: 0.95 }),
  neon: feel({ label: "neon", icon: "wb-neon", group: "live", thin: 0.5, smooth: 0.5, anim: "neon", size: 10, opacity: 1 }),
  boil: feel({ label: "boil", icon: "wb-boil", group: "live", thin: 0.3, smooth: 0.35, anim: "boil", size: 6, opacity: 1 }),
  wave: feel({ label: "wave", icon: "wb-wave", group: "live", thin: 0.5, smooth: 0.5, anim: "wave", size: 8, opacity: 1 }),
  flame: feel({ label: "flame", icon: "wb-flame", group: "live", thin: 0.25, smooth: 0.5, soft: 0.4, anim: "flame", size: 22, opacity: 0.95 }),
  march: feel({ label: "march", icon: "wb-march", group: "live", thin: 1, pressure: 0, smooth: 0.45, anim: "march", size: 6, opacity: 1 }),
  glitch: feel({ label: "glitch", icon: "wb-glitch", group: "live", thin: 0.5, smooth: 0.3, anim: "glitch", size: 10, opacity: 1 }),
  pulse: feel({ label: "pulse", icon: "wb-pulse", group: "live", thin: 0.6, pressure: 0.5, smooth: 0.5, anim: "pulse", size: 14, opacity: 1 }),

  smudge: feel({ label: "smudge", icon: "wb-smudge", group: "retouch", pressure: 0.6, smooth: 0.3, soft: 0.5, size: 30, opacity: 0.7 }),
  blend: feel({ label: "blend", icon: "wb-blend", group: "retouch", pressure: 0.6, smooth: 0.35, soft: 0.5, size: 30, opacity: 0.6 }),
  blur: feel({ label: "blur", icon: "wb-blur", group: "retouch", pressure: 0.6, smooth: 0.3, soft: 0.7, size: 40, opacity: 0.6 }),
  lighten: feel({ label: "lighten", icon: "wb-lighten", group: "retouch", pressure: 0.7, smooth: 0.3, soft: 0.7, size: 40, opacity: 0.35 }),
  darken: feel({ label: "darken", icon: "wb-darken", group: "retouch", pressure: 0.7, smooth: 0.3, soft: 0.7, size: 40, opacity: 0.35 }),

  fill: feel({ label: "fill", icon: "wb-fill", group: "fill", pressure: 0, smooth: 0, size: 1, opacity: 1 })
};

export const GROUPS: Array<{ id: BrushGroup; label: string }> = [
  { id: "draw", label: "draw" },
  { id: "paint", label: "paint" },
  { id: "texture", label: "texture" },
  { id: "live", label: "live" },
  { id: "retouch", label: "retouch" }
];

export function brushesIn(group: BrushGroup): BrushTool[] {
  return BOARD_TOOLS.filter((tool) => TOOL_FEEL[tool].group === group);
}

export interface BrushSettings {
  smooth: number;
  press: number;
  soft: number;
  gap: number;
}

export function brushDefaults(tool: BrushTool): BrushSettings {
  const base = TOOL_FEEL[tool];
  return { smooth: base.smooth, press: base.pressure, soft: base.soft, gap: base.gap };
}

export const FONT_STACK: Record<TextFont, string> = {
  sans: "system-ui, -apple-system, 'Segoe UI', sans-serif",
  serif: "Georgia, 'Times New Roman', serif",
  mono: "ui-monospace, Menlo, Consolas, monospace",
  display: "'Chakra Petch', 'Arial Narrow', system-ui, sans-serif"
};

export interface Box {
  x0: number;
  y0: number;
  x1: number;
  y1: number;
}

const feelOf = (stroke: Pick<BoardStroke, "tool">): ToolFeel | null => (stroke.tool === "text" ? null : TOOL_FEEL[stroke.tool]);

export function isAnimated(stroke: Pick<BoardStroke, "tool">): boolean {
  return Boolean(feelOf(stroke)?.anim);
}

function seeded(text: string): () => number {
  let state = 2166136261;
  for (let at = 0; at < text.length; at++) state = Math.imul(state ^ text.charCodeAt(at), 16777619);
  return () => {
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// --- text

const ruler = typeof document !== "undefined" ? document.createElement("canvas").getContext("2d") : null;

function textLines(stroke: BoardStroke): string[] {
  return String(stroke.text || "").split("\n");
}

export function textBox(stroke: BoardStroke): Box {
  const lines = textLines(stroke);
  let widest = 0;
  if (ruler) {
    ruler.font = `${stroke.size}px ${FONT_STACK[stroke.font || "sans"]}`;
    lines.forEach((line) => { widest = Math.max(widest, ruler.measureText(line).width); });
  } else {
    lines.forEach((line) => { widest = Math.max(widest, line.length * stroke.size * 0.6); });
  }
  return { x0: stroke.pts[0], y0: stroke.pts[1], x1: stroke.pts[0] + Math.max(widest, stroke.size * 0.5), y1: stroke.pts[1] + lines.length * stroke.size * 1.25 };
}

function paintText(ctx: CanvasRenderingContext2D, stroke: BoardStroke, alpha: number): void {
  ctx.globalAlpha = alpha;
  ctx.fillStyle = stroke.color;
  ctx.font = `${stroke.size}px ${FONT_STACK[stroke.font || "sans"]}`;
  ctx.textBaseline = "top";
  textLines(stroke).forEach((line, at) => ctx.fillText(line, stroke.pts[0], stroke.pts[1] + at * stroke.size * 1.25));
  ctx.globalAlpha = 1;
}

// --- shapes

function resolved(stroke: Pick<BoardStroke, "tool" | "press" | "soft" | "gap">): { press: number; soft: number; gap: number } {
  const base = TOOL_FEEL[stroke.tool as BrushTool];
  return { press: stroke.press ?? base.pressure, soft: stroke.soft ?? base.soft, gap: stroke.gap ?? base.gap };
}

function radii(stroke: BoardStroke, grow: number): number[] {
  const base = TOOL_FEEL[stroke.tool as BrushTool];
  const { press } = resolved(stroke);
  const count = stroke.pts.length / 3;
  const half = (stroke.size / 2) * grow;
  const taperLength = Math.min(12, Math.floor(count / 3));
  const out: number[] = [];
  for (let at = 0; at < count; at++) {
    const pressure = 1 - press + press * stroke.pts[at * 3 + 2];
    let radius = half * (base.thin + (1 - base.thin) * pressure);
    if (base.taper && taperLength > 1) {
      const end = Math.min(at, count - 1 - at);
      if (end < taperLength) radius *= 0.15 + 0.85 * (end / taperLength);
    }
    out.push(Math.max(0.3, radius));
  }
  return out;
}

export function strokeBox(stroke: BoardStroke): Box {
  if (stroke.tool === "text") return textBox(stroke);
  if (stroke.tool === "fill") {
    const box = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
    for (let at = 0; at < stroke.pts.length; at += 3) {
      box.x0 = Math.min(box.x0, stroke.pts[at] - 1);
      box.y0 = Math.min(box.y0, stroke.pts[at + 1] - 1);
      box.x1 = Math.max(box.x1, stroke.pts[at] + 1);
      box.y1 = Math.max(box.y1, stroke.pts[at + 1] + 1);
    }
    return box;
  }
  const base = TOOL_FEEL[stroke.tool];
  const box = { x0: Infinity, y0: Infinity, x1: -Infinity, y1: -Infinity };
  const pad = (stroke.size / 2) * (base.anim === "neon" ? 1.9 : 1) + (base.scatter ? stroke.size * 0.1 : 0) + 1;
  for (let at = 0; at < stroke.pts.length; at += 3) {
    box.x0 = Math.min(box.x0, stroke.pts[at] - pad);
    box.y0 = Math.min(box.y0, stroke.pts[at + 1] - pad);
    box.x1 = Math.max(box.x1, stroke.pts[at] + pad);
    box.y1 = Math.max(box.y1, stroke.pts[at + 1] + pad);
  }
  return box;
}

export function boxesMeet(a: Box, b: Box): boolean {
  return a.x0 <= b.x1 && a.x1 >= b.x0 && a.y0 <= b.y1 && a.y1 >= b.y0;
}

export function joinBoxes(a: Box | null, b: Box): Box {
  return a ? { x0: Math.min(a.x0, b.x0), y0: Math.min(a.y0, b.y0), x1: Math.max(a.x1, b.x1), y1: Math.max(a.y1, b.y1) } : { ...b };
}

// the line: circles at every point, and a four sided bridge between neighbours
function linePath(stroke: BoardStroke, grow: number, shaped?: { pts: number[]; r: number[] }): Path2D {
  const path = new Path2D();
  const r = shaped ? shaped.r : radii(stroke, grow);
  const p = shaped ? shaped.pts : stroke.pts;
  const count = r.length;
  for (let at = 0; at < count; at++) {
    const x = p[at * 3];
    const y = p[at * 3 + 1];
    path.moveTo(x + r[at], y);
    path.arc(x, y, r[at], 0, Math.PI * 2);
    if (at === count - 1) break;
    const nx = p[at * 3 + 3];
    const ny = p[at * 3 + 4];
    const dx = nx - x;
    const dy = ny - y;
    const distance = Math.hypot(dx, dy);
    // one circle inside the other needs no bridge
    if (distance <= Math.abs(r[at] - r[at + 1])) continue;
    const angle = Math.atan2(dy, dx);
    const spread = Math.acos((r[at] - r[at + 1]) / distance);
    const quad = [
      [x + r[at] * Math.cos(angle + spread), y + r[at] * Math.sin(angle + spread)],
      [nx + r[at + 1] * Math.cos(angle + spread), ny + r[at + 1] * Math.sin(angle + spread)],
      [nx + r[at + 1] * Math.cos(angle - spread), ny + r[at + 1] * Math.sin(angle - spread)],
      [x + r[at] * Math.cos(angle - spread), y + r[at] * Math.sin(angle - spread)]
    ];
    // every piece winds the same way as the circles so the nonzero fill joins them instead of punching holes
    let area = 0;
    for (let corner = 0; corner < 4; corner++) {
      const [ax, ay] = quad[corner];
      const [bx, by] = quad[(corner + 1) % 4];
      area += ax * by - bx * ay;
    }
    if (area < 0) quad.reverse();
    path.moveTo(quad[0][0], quad[0][1]);
    for (let corner = 1; corner < 4; corner++) path.lineTo(quad[corner][0], quad[corner][1]);
    path.closePath();
  }
  return path;
}

const MAX_DOTS = 30_000;

// scattered dots: walk along the line, and at every step throw a handful of dots around it. the same stroke
// always throws the same dots because the throw is seeded by the stroke's id
function dotsPath(stroke: BoardStroke): Path2D {
  const base = TOOL_FEEL[stroke.tool as BrushTool];
  const { press, gap } = resolved(stroke);
  const rand = seeded(stroke.id);
  const path = new Path2D();
  const half = stroke.size / 2;
  const step = Math.max(0.6, stroke.size * (0.04 + gap * 0.4));
  const per = base.scatter === "air" ? 34 : base.scatter === "spray" ? 6 + Math.min(10, Math.round(stroke.size / 8)) : 3;
  const dot = Math.max(0.35, stroke.size * (base.scatter === "air" ? 0.013 : base.scatter === "spray" ? 0.035 : 0.1));
  let made = 0;
  const stamp = (x: number, y: number, pressure: number) => {
    const reach = half * (base.thin + (1 - base.thin) * (1 - press + press * pressure));
    for (let at = 0; at < per && made < MAX_DOTS; at++) {
      const angle = rand() * Math.PI * 2;
      // the air brush is dense in the middle, the others spread evenly
      const spread = base.scatter === "air" ? Math.pow(rand(), 1.7) : Math.sqrt(rand());
      const size = dot * (0.6 + rand() * 0.8);
      const dx = x + Math.cos(angle) * reach * spread;
      const dy = y + Math.sin(angle) * reach * spread;
      path.moveTo(dx + size, dy);
      path.arc(dx, dy, size, 0, Math.PI * 2);
      made++;
    }
  };
  const p = stroke.pts;
  stamp(p[0], p[1], p[2]);
  let carried = 0;
  for (let at = 3; at + 2 < p.length; at += 3) {
    const dx = p[at] - p[at - 3];
    const dy = p[at + 1] - p[at - 2];
    const length = Math.hypot(dx, dy);
    if (!length) continue;
    let along = step - carried;
    while (along <= length) {
      const share = along / length;
      stamp(p[at - 3] + dx * share, p[at - 2] + dy * share, p[at - 1] + (p[at + 2] - p[at - 1]) * share);
      along += step;
    }
    carried = length - (along - step);
  }
  return path;
}

// the moving brushes wobble their line in a few fixed ways and cycle through them, so each wobble is a shape
// built once like any other. how many wobbles and how long each shows
const VARIANTS: Partial<Record<Anim, { count: number; ms: number }>> = {
  boil: { count: 3, ms: 110 },
  wave: { count: 12, ms: 70 },
  flame: { count: 4, ms: 95 },
  pulse: { count: 10, ms: 80 }
};

export function variantAt(stroke: Pick<BoardStroke, "tool">, time: number): number {
  const anim = stroke.tool === "text" ? "" : TOOL_FEEL[stroke.tool].anim;
  const spec = anim ? VARIANTS[anim] : undefined;
  return spec ? Math.floor(time / spec.ms) % spec.count : 0;
}

function wobbled(stroke: BoardStroke, grow: number, variant: number): Path2D {
  const anim = TOOL_FEEL[stroke.tool as BrushTool].anim;
  const base = radii(stroke, grow);
  const p = stroke.pts;
  const count = base.length;
  const pts = p.slice();
  const rand = seeded(`${stroke.id}${anim}${anim === "wave" || anim === "pulse" ? 0 : variant}`);
  const a1 = rand() * 6.28;
  const a2 = rand() * 6.28;
  const a3 = rand() * 6.28;
  const amp = stroke.size * (anim === "boil" ? 0.45 : anim === "flame" ? 0.55 : 0.7);
  const turn = (variant / (VARIANTS[anim]?.count || 1)) * Math.PI * 2;
  for (let at = 0; at < count; at++) {
    const prev = Math.max(0, at - 1) * 3;
    const next = Math.min(count - 1, at + 1) * 3;
    const tx = p[next] - p[prev];
    const ty = p[next + 1] - p[prev + 1];
    const length = Math.hypot(tx, ty) || 1;
    const nx = -ty / length;
    const ny = tx / length;
    if (anim === "boil") {
      // a line that shivers, three slightly different lines shown in turn
      const shake = amp * (Math.sin(at * 0.35 + a1) + 0.5 * Math.sin(at * 0.9 + a2));
      pts[at * 3] += nx * shake + Math.cos(a3) * amp * 0.3;
      pts[at * 3 + 1] += ny * shake + Math.sin(a3) * amp * 0.3;
    } else if (anim === "wave") {
      const shake = amp * Math.sin(at * 0.45 - turn);
      pts[at * 3] += nx * shake;
      pts[at * 3 + 1] += ny * shake;
    } else if (anim === "flame") {
      // flames lick upwards, more the further along the line
      const lick = amp * (0.5 + 0.5 * Math.sin(at * 0.55 + a1)) * (0.5 + 0.5 * Math.sin(at * 0.21 + a2));
      pts[at * 3] += Math.sin(at * 0.9 + a3) * amp * 0.25;
      pts[at * 3 + 1] -= lick;
    } else if (anim === "pulse") {
      base[at] *= 1 + 0.55 * Math.sin(at * 0.4 - turn);
    }
  }
  return linePath(stroke, grow, { pts, r: base });
}

// the dashes of the marching brush follow a plain line through the points
function polyline(stroke: BoardStroke): Path2D {
  const path = new Path2D();
  const p = stroke.pts;
  path.moveTo(p[0], p[1]);
  if (p.length < 6) path.lineTo(p[0] + 0.01, p[1]);
  for (let at = 3; at + 1 < p.length; at += 3) path.lineTo(p[at], p[at + 1]);
  return path;
}

const shapes = new WeakMap<BoardStroke, Map<number, Path2D>>();

// strokes never change once drawn, so each shape is built once and reused at every zoom. a moved stroke is a
// new object and builds its own
export function cachedPath(stroke: BoardStroke, grow = 1, variant = 0): Path2D {
  let byGrow = shapes.get(stroke);
  if (!byGrow) {
    byGrow = new Map();
    shapes.set(stroke, byGrow);
  }
  const key = Math.round(grow * 100) + variant * 100_000;
  let path = byGrow.get(key);
  if (!path) {
    const base = TOOL_FEEL[stroke.tool as BrushTool];
    if (base.scatter) path = dotsPath(stroke);
    else if (base.anim === "march") path = polyline(stroke);
    else if (VARIANTS[base.anim]) path = wobbled(stroke, grow, variant);
    else path = linePath(stroke, grow);
    byGrow.set(key, path);
  }
  return path;
}

// a stroke still being drawn keeps changing, so whatever was built for it so far is thrown away before it is painted
export function forgetStroke(stroke: BoardStroke): void {
  shapes.delete(stroke);
  sparks.delete(stroke);
}

// --- textures: the colour with tiny holes in it, made once per colour

const tiles = new Map<string, CanvasPattern | null>();

function tileFor(ctx: CanvasRenderingContext2D, texture: Texture, color: string): CanvasPattern | string {
  const key = `${texture}|${color}`;
  if (!tiles.has(key)) {
    const size = texture === "charcoal" ? 96 : 64;
    const tile = document.createElement("canvas");
    tile.width = size;
    tile.height = size;
    const paint = tile.getContext("2d");
    if (paint) {
      const rand = seeded(texture);
      paint.fillStyle = color;
      paint.fillRect(0, 0, size, size);
      paint.globalCompositeOperation = "destination-out";
      const dots = (count: number, min: number, max: number, wide: number) => {
        for (let at = 0; at < count; at++) {
          paint.globalAlpha = min + rand() * (max - min);
          paint.fillRect(Math.floor(rand() * size), Math.floor(rand() * size), wide, wide);
        }
      };
      const blobs = (count: number, radius: number, min: number, max: number) => {
        for (let at = 0; at < count; at++) {
          paint.globalAlpha = min + rand() * (max - min);
          paint.beginPath();
          paint.arc(rand() * size, rand() * size, 1 + rand() * radius, 0, Math.PI * 2);
          paint.fill();
        }
      };
      if (texture === "grain") dots(430, 0.25, 0.95, 1);
      if (texture === "chalk") dots(900, 0.4, 1, 1), dots(160, 0.3, 0.9, 2);
      if (texture === "charcoal") blobs(70, 3, 0.15, 0.5), dots(900, 0.3, 0.9, 1);
      if (texture === "paper") blobs(46, 11, 0.04, 0.16), dots(300, 0.08, 0.25, 1);
      if (texture === "crayon") {
        paint.lineWidth = 1;
        paint.strokeStyle = "#000";
        for (let at = 0; at < 150; at++) {
          paint.globalAlpha = 0.3 + rand() * 0.5;
          const x = rand() * size;
          const y = rand() * size;
          paint.beginPath();
          paint.moveTo(x, y);
          paint.lineTo(x + 5 + rand() * 14, y + 3 + rand() * 8);
          paint.stroke();
        }
        dots(120, 0.3, 0.8, 1);
      }
    }
    tiles.set(key, paint ? ctx.createPattern(tile, "repeat") : null);
  }
  return tiles.get(key) || color;
}

// --- painting

const RAINBOW = ["#ff3b6b", "#ff9f1c", "#ffe14d", "#3ddc84", "#27c4ff", "#7b5cff", "#ff3b6b"];

function rainbowFill(ctx: CanvasRenderingContext2D, box: Box, time: number): CanvasGradient {
  const period = Math.max(160, Math.hypot(box.x1 - box.x0, box.y1 - box.y0) / 2);
  const shift = ((time / 1400) % 1) * period;
  const gradient = ctx.createLinearGradient(box.x0 - period + shift, box.y0, box.x0 + period * 2 + shift, box.y1);
  for (let at = 0; at <= 18; at++) gradient.addColorStop(at / 18, RAINBOW[at % 6 === 0 && at ? 6 : at % 6]);
  return gradient;
}

const FLAME = ["#fff6b0", "#ffc43a", "#ff7a1a", "#e8321c", "#8f1010"];

function flameFill(ctx: CanvasRenderingContext2D, box: Box, time: number): CanvasGradient {
  const height = Math.max(40, box.y1 - box.y0);
  const sway = Math.sin(time / 380) * height * 0.08;
  const gradient = ctx.createLinearGradient(0, box.y0 - sway, 0, box.y1 + height * 0.15 + sway);
  FLAME.forEach((color, at) => gradient.addColorStop(at / (FLAME.length - 1), color));
  return gradient;
}

interface Spark {
  x: number;
  y: number;
  r: number;
  phase: number;
}

const sparks = new WeakMap<BoardStroke, Spark[]>();

function sparklesOf(stroke: BoardStroke): Spark[] {
  let list = sparks.get(stroke);
  if (list) return list;
  list = [];
  const rand = seeded(stroke.id);
  const step = Math.max(2, stroke.size * 0.38);
  const p = stroke.pts;
  let carried = 0;
  for (let at = 3; at + 2 < p.length && list.length < 500; at += 3) {
    const dx = p[at] - p[at - 3];
    const dy = p[at + 1] - p[at - 2];
    const length = Math.hypot(dx, dy);
    let along = step - carried;
    while (along <= length && list.length < 500) {
      const share = along / length;
      const angle = rand() * Math.PI * 2;
      const reach = (stroke.size / 2) * Math.sqrt(rand());
      list.push({ x: p[at - 3] + dx * share + Math.cos(angle) * reach, y: p[at - 2] + dy * share + Math.sin(angle) * reach, r: stroke.size * (0.07 + rand() * 0.11), phase: rand() * 6.28 });
      along += step;
    }
    carried = length - (along - step);
  }
  if (!list.length) list.push({ x: p[0], y: p[1], r: stroke.size * 0.08, phase: 0 });
  sparks.set(stroke, list);
  return list;
}

function softPasses(stroke: BoardStroke, base: ToolFeel): Array<[number, number]> {
  const { soft } = resolved(stroke);
  if (base.scatter || soft < 0.03) return [[1, 1]];
  // rings from the outside in, each a little more of the colour, the last one all of it
  const rings: Array<[number, number]> = [];
  for (let at = 0; at < 6; at++) rings.push([1 - (soft * 0.75 * at) / 5, at === 5 ? 1 : 0.2]);
  return rings;
}

export function retouchLook(stroke: BoardStroke): RetouchLook {
  const { press, soft } = resolved(stroke);
  return { soft, press };
}

// paints whatever points of a retouch stroke have come since last time, onto the layer it works on
export function continueRetouch(ctx: CanvasRenderingContext2D, stroke: BoardStroke, state: RetouchState): void {
  retouchStroke(ctx, stroke, stroke.tool as RetouchKind, retouchLook(stroke), stroke.opacity, state);
}

export { freshRetouch };
export type { RetouchState };

// time only matters to the live brushes. alpha is the stroke's own, or 1 when the stroke is being drawn on
// a canvas that already carries the opacity
export function paintStroke(ctx: CanvasRenderingContext2D, stroke: BoardStroke, time = 0, alpha = stroke.opacity): void {
  if (stroke.tool === "text") {
    paintText(ctx, stroke, alpha);
    return;
  }
  const base = TOOL_FEEL[stroke.tool];
  if (stroke.tool === "fill") {
    // the lasso fill: the outline, filled
    const area = new Path2D();
    area.moveTo(stroke.pts[0], stroke.pts[1]);
    for (let at = 3; at + 1 < stroke.pts.length; at += 3) area.lineTo(stroke.pts[at], stroke.pts[at + 1]);
    area.closePath();
    ctx.globalCompositeOperation = "source-over";
    ctx.fillStyle = stroke.color;
    ctx.globalAlpha = alpha;
    ctx.fill(area, "nonzero");
    ctx.globalAlpha = 1;
    return;
  }
  if (base.group === "retouch") {
    retouchStroke(ctx, stroke, stroke.tool as RetouchKind, retouchLook(stroke), alpha, freshRetouch());
    return;
  }
  ctx.globalCompositeOperation = base.blend;
  if (base.anim === "neon") {
    const pulse = 0.5 + 0.5 * Math.sin(time / 330);
    ctx.fillStyle = stroke.color;
    ctx.globalAlpha = alpha * (0.14 + 0.12 * pulse);
    ctx.fill(cachedPath(stroke, 1.9), "nonzero");
    ctx.globalAlpha = alpha * (0.3 + 0.25 * pulse);
    ctx.fill(cachedPath(stroke, 1.35), "nonzero");
    ctx.globalAlpha = alpha;
    ctx.fill(cachedPath(stroke, 0.8), "nonzero");
    ctx.fillStyle = "#ffffff";
    ctx.globalAlpha = alpha * (0.75 + 0.2 * pulse);
    ctx.fill(cachedPath(stroke, 0.38), "nonzero");
  } else if (base.anim === "march") {
    // dashes of the colour and of white take turns along the line
    const dash = Math.max(3, stroke.size * 1.4);
    ctx.lineCap = "butt";
    ctx.lineJoin = "round";
    ctx.lineWidth = stroke.size;
    const line = cachedPath(stroke);
    ctx.setLineDash([dash, dash]);
    ctx.lineDashOffset = -(time / 45) % (dash * 2);
    ctx.strokeStyle = stroke.color;
    ctx.globalAlpha = alpha;
    ctx.stroke(line);
    ctx.lineDashOffset = -(time / 45) % (dash * 2) - dash;
    ctx.strokeStyle = "#ffffff";
    ctx.stroke(line);
    ctx.setLineDash([]);
  } else if (base.anim === "glitch") {
    // red and blue copies slip sideways in short jumps, now and then everything snaps back
    const slot = Math.floor(time / 120);
    const rand = seeded(`${stroke.id}${slot}`);
    const reach = rand() < 0.35 ? 0 : stroke.size * (0.3 + rand() * 0.9);
    const lift = (rand() - 0.5) * stroke.size * 0.4;
    const line = cachedPath(stroke);
    ctx.globalAlpha = alpha;
    if (reach) {
      ctx.globalCompositeOperation = "lighter";
      ctx.fillStyle = "#ff2d55";
      ctx.save();
      ctx.translate(reach, lift);
      ctx.fill(line, "nonzero");
      ctx.restore();
      ctx.fillStyle = "#18d6ff";
      ctx.save();
      ctx.translate(-reach, -lift);
      ctx.fill(line, "nonzero");
      ctx.restore();
      ctx.globalCompositeOperation = "source-over";
    }
    ctx.fillStyle = stroke.color;
    ctx.fill(line, "nonzero");
  } else {
    const wobble = variantAt(stroke, time);
    const fill = base.anim === "rainbow"
      ? rainbowFill(ctx, strokeBox(stroke), time)
      : base.anim === "flame" ? flameFill(ctx, strokeBox(stroke), time)
      : base.texture !== "none" ? tileFor(ctx, base.texture, stroke.color) : stroke.color;
    ctx.fillStyle = fill;
    for (const [grow, weight] of softPasses(stroke, base)) {
      ctx.globalAlpha = alpha * weight;
      ctx.fill(cachedPath(stroke, grow, wobble), "nonzero");
    }
    if (base.anim === "glitter") {
      ctx.fillStyle = "#ffffff";
      for (const spark of sparklesOf(stroke)) {
        const twinkle = Math.max(0, Math.sin(time / 210 + spark.phase));
        if (twinkle < 0.05) continue;
        ctx.globalAlpha = alpha * twinkle;
        ctx.beginPath();
        ctx.arc(spark.x, spark.y, spark.r * (0.5 + twinkle), 0, Math.PI * 2);
        ctx.fill();
      }
    }
  }
  ctx.globalCompositeOperation = "source-over";
  ctx.globalAlpha = 1;
}

// smooths the pen as it moves: each new point is pulled part of the way towards where the pen is
export function smoothPoint(smooth: number, last: [number, number] | null, x: number, y: number): [number, number] {
  if (!last) return [x, y];
  const keep = smooth * 0.8;
  return [last[0] * keep + x * (1 - keep), last[1] * keep + y * (1 - keep)];
}

// --- hit tests, for the stroke eraser and picking things up

function reach(px: number, py: number, ax: number, ay: number, bx: number, by: number): number {
  const dx = bx - ax;
  const dy = by - ay;
  const length = dx * dx + dy * dy;
  const share = length ? Math.max(0, Math.min(1, ((px - ax) * dx + (py - ay) * dy) / length)) : 0;
  return Math.hypot(px - (ax + dx * share), py - (ay + dy * share));
}

// whether a point is inside a flat x, y, pressure outline
export function insideOutline(pts: number[], x: number, y: number): boolean {
  let inside = false;
  const count = pts.length / 3;
  for (let at = 0, back = count - 1; at < count; back = at++) {
    const ax = pts[at * 3];
    const ay = pts[at * 3 + 1];
    const bx = pts[back * 3];
    const by = pts[back * 3 + 1];
    if (ay > y !== by > y && x < ((bx - ax) * (y - ay)) / (by - ay) + ax) inside = !inside;
  }
  return inside;
}

export function strokeHit(stroke: BoardStroke, x: number, y: number, pad: number): boolean {
  if (isPixelTool(stroke.tool)) return false;
  if (stroke.tool === "fill") return insideOutline(stroke.pts, x, y);
  if (stroke.tool === "text") {
    const box = textBox(stroke);
    return x >= box.x0 - pad && x <= box.x1 + pad && y >= box.y0 - pad && y <= box.y1 + pad;
  }
  const near = stroke.size / 2 + pad;
  const p = stroke.pts;
  if (p.length < 6) return Math.hypot(x - p[0], y - p[1]) <= near;
  for (let at = 3; at + 2 < p.length; at += 3) {
    if (reach(x, y, p[at - 3], p[at - 2], p[at], p[at + 1]) <= near) return true;
  }
  return false;
}

// a sample squiggle in a brush's own style, for the brush shelf
export function sampleStroke(tool: BrushTool, width: number, height: number, size: number, color: string, settings: BrushSettings): BoardStroke {
  const pts: number[] = [];
  const count = 36;
  for (let at = 0; at <= count; at++) {
    const share = at / count;
    pts.push(width * (0.1 + 0.8 * share), height * (0.5 + 0.2 * Math.sin(share * 7 + 1)), 0.25 + 0.7 * Math.sin(share * Math.PI));
  }
  return { id: `sample${tool}`, layer: "", tool, color, size, opacity: TOOL_FEEL[tool].opacity, pts, by: "", at: 0, ...settings };
}

export type { BoardTool };
